import React from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Image,
  StyleSheet,
  LayoutChangeEvent,
} from "react-native";
import { NormalText } from "../../../../../common/native";
import { TCropCoords } from "../../../../../../utils/types";
import { TSideBySideProcessedImage } from "../../../../../../services/api-clinic";

type SwapImageModalContentProps = {
  isBeforeImageSelection: boolean;
  setIsBeforeImageSelection: (value: boolean) => void;
  onClose: () => void;
  swapImagesModalData: {
    handleSetAfterCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords?: TCropCoords,
    ) => void;
    handleSetBeforeCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords?: TCropCoords,
    ) => void;
    templatesData: any[];
    currentTemplateIndex: number;
    uploadedImages: { preOp: Blob[]; postOp: Blob[] };
    beforeImageOptions: TSideBySideProcessedImage[];
    afterImageOptions: TSideBySideProcessedImage[];
    processedImageUrls: {
      preOp: string[];
      postOp: string[];
    };
  };
  onImageSelect: (
    imageUrl: string,
    isBeforeImage: boolean,
    selectedImage: TSideBySideProcessedImage,
  ) => void;
};

const SwapImageModalContent: React.FC<SwapImageModalContentProps> = ({
  isBeforeImageSelection,
  setIsBeforeImageSelection,
  onClose,
  swapImagesModalData,
  onImageSelect,
}) => {
  const [beforeViewportHeight, setBeforeViewportHeight] = React.useState(0);
  const [beforeContentHeight, setBeforeContentHeight] = React.useState(0);
  const [afterViewportHeight, setAfterViewportHeight] = React.useState(0);
  const [afterContentHeight, setAfterContentHeight] = React.useState(0);

  const beforeImages = swapImagesModalData.beforeImageOptions || [];
  const afterImages = swapImagesModalData.afterImageOptions || [];

  const currentTemplate =
    swapImagesModalData.templatesData[swapImagesModalData.currentTemplateIndex];
  const shouldEnableBeforeScroll =
    beforeContentHeight > beforeViewportHeight + 1 && beforeViewportHeight > 0;
  const shouldEnableAfterScroll =
    afterContentHeight > afterViewportHeight + 1 && afterViewportHeight > 0;

  const handleBeforeLayout = React.useCallback((event: LayoutChangeEvent) => {
    setBeforeViewportHeight(event.nativeEvent.layout.height);
  }, []);

  const handleAfterLayout = React.useCallback((event: LayoutChangeEvent) => {
    setAfterViewportHeight(event.nativeEvent.layout.height);
  }, []);

  return (
    <View style={styles.modalContent}>
      <View style={styles.modalHeader}>
        <NormalText text="Swap Images" style={styles.modalTitle} />
        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
          <NormalText text="✕" style={styles.closeButtonText} />
        </TouchableOpacity>
      </View>

      <View style={styles.columnsContainer}>
        {/* Before Images */}
        <View style={styles.column}>
          <NormalText text="Before Image" style={styles.columnTitle} />
          <ScrollView
            style={styles.imageScrollView}
            contentContainerStyle={styles.imageGrid}
            onLayout={handleBeforeLayout}
            onContentSizeChange={(_, contentHeight) =>
              setBeforeContentHeight(contentHeight)
            }
            scrollEnabled={shouldEnableBeforeScroll}
            showsVerticalScrollIndicator={shouldEnableBeforeScroll}
          >
            {beforeImages.map((image, index) => {
              const imageUrl = image.processed_get_url;
              const isActive = imageUrl === currentTemplate.beforeOriginalImage;
              return (
                <TouchableOpacity
                  key={`before-${index}`}
                  style={[
                    styles.imageContainer,
                    isActive && styles.activeImage,
                  ]}
                  onPress={() => {
                    // Automatically set to before image selection when clicking in this section
                    setIsBeforeImageSelection(true);
                    onImageSelect(imageUrl, true, image);
                  }}
                >
                  <Image
                    source={{ uri: imageUrl }}
                    style={styles.thumbnailImage}
                    resizeMode="cover"
                  />
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* After Images */}
        <View style={styles.column}>
          <NormalText text="After Image" style={styles.columnTitle} />
          <ScrollView
            style={styles.imageScrollView}
            contentContainerStyle={styles.imageGrid}
            onLayout={handleAfterLayout}
            onContentSizeChange={(_, contentHeight) =>
              setAfterContentHeight(contentHeight)
            }
            scrollEnabled={shouldEnableAfterScroll}
            showsVerticalScrollIndicator={shouldEnableAfterScroll}
          >
            {afterImages.map((image, index) => {
              const imageUrl = image.processed_get_url;
              const isActive = imageUrl === currentTemplate.afterOriginalImage;
              return (
                <TouchableOpacity
                  key={`after-${index}`}
                  style={[
                    styles.imageContainer,
                    isActive && styles.activeImage,
                  ]}
                  onPress={() => {
                    // Automatically set to after image selection when clicking in this section
                    setIsBeforeImageSelection(false);
                    onImageSelect(imageUrl, false, image);
                  }}
                >
                  <Image
                    source={{ uri: imageUrl }}
                    style={styles.thumbnailImage}
                    resizeMode="cover"
                  />
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  modalHeader: {
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    paddingBottom: 6,
  },
  modalTitle: {
    fontSize: 14,
    fontWeight: "bold",
  },
  closeButton: {
    position: "absolute",
    right: 0,
    top: 0,
    padding: 3,
  },
  closeButtonText: {
    fontSize: 14,
    fontWeight: "bold",
  },
  columnsContainer: {
    flexDirection: "row",
    flex: 1,
    minHeight: 0,
    gap: 12,
    justifyContent: "space-between",
  },
  column: {
    flex: 1,
    minHeight: 0,
    borderWidth: 1,
    borderColor: "#DDDEE0",
    borderRadius: 6,
    padding: 6,
    alignItems: "center",
  },
  imageScrollView: {
    flex: 1,
    minHeight: 0,
    width: "100%",
  },
  columnTitle: {
    fontWeight: "600",
    marginBottom: 6,
    fontSize: 12,
  },
  imageGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  imageContainer: {
    width: 72,
    height: 72,
    margin: 4,
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 4,
    overflow: "hidden",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    borderRadius: 4,
    backgroundColor: "#ccc",
  },
  activeImage: {
    borderColor: "#00b7f4",
    borderWidth: 2,
  },
});

export default SwapImageModalContent;
